import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PostService from "../api/PostService_old";
import '../screens/css/MyDocuments.css';
import { API_URL } from '../config/app-config';
import pdfIcon from "../images/pdf.png";
import docIcon from "../images/doc.png";
import folderIcon from "../images/folder.png";
import { MdDelete } from "react-icons/md";
import { MdOutlineEdit } from "react-icons/md";

// Custom confirmation dialog component
const ConfirmDialog = ({ visible, message, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <div className="confirm-dialog-overlay">
      <div className="confirm-dialog">
        <p>{message}</p>
        <div className="confirm-dialog-buttons">
          <button className="confirm-btn" onClick={onConfirm}>Yes, Delete</button>
          <button className="cancel-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const MyDocuments = () => {
  // Upload States
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [tags, setTags] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');

  // Folder and Document View States
  const [uploadedDocuments, setUploadedDocuments] = useState([]); // Top-level folders with documents
  const [filteredDocuments, setFilteredDocuments] = useState([]); // Filtered folder list (by search)
  const [folders, setFolders] = useState([]); // For folder selection in upload

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination for Folder View
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // State to track if user is "inside" a folder (viewing its documents)
  const [currentFolder, setCurrentFolder] = useState(null);

  // Section Toggle: 'upload' or 'view'
  const [activeSection, setActiveSection] = useState('upload');

  // Search for documents inside a folder
  const [docSearchQuery, setDocSearchQuery] = useState("");
  const [filteredFolderDocuments, setFilteredFolderDocuments] = useState([]);

  // States for inline editing
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editName, setEditName] = useState('');

  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadedMB, setUploadedMB] = useState(0);
  const [totalMB, setTotalMB] = useState(0);

  // State for our custom confirm dialog
  const [confirmData, setConfirmData] = useState({
    visible: false,
    message: '',
    onConfirm: null
  });

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    if (activeSection === 'view' && !currentFolder) {
      fetchDocuments(0);
    }
  }, [activeSection, currentFolder]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredDocuments(uploadedDocuments);
      return;
    }
    const filtered = uploadedDocuments.filter((folder) =>
      folder.folderName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredDocuments(filtered);
  }, [searchQuery, uploadedDocuments]);

  useEffect(() => {
    if (currentFolder && currentFolder.documents) {
      if (!docSearchQuery) {
        setFilteredFolderDocuments(currentFolder.documents);
      } else {
        setFilteredFolderDocuments(
          currentFolder.documents.filter((doc) =>
            doc.name.toLowerCase().includes(docSearchQuery.toLowerCase()) ||
            (doc.tags && doc.tags.toLowerCase().includes(docSearchQuery.toLowerCase()))
          )
        );
      }
    }
  }, [docSearchQuery, currentFolder]);

  const fetchFolders = async () => {
    try {
      const response = await PostService.getFolders();
      if (response?.data?.status === 1) {
        setFolders(response.data.data);
      }
    } catch (error) {
      toast.error("Error fetching folders!", { position: "top-right" });
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      console.log("Fetching all documents without pagination");

      // Use a large number to get all documents at once, or check if your API supports no pagination
      const response = await PostService.getDocumentsByUser(0, 1000); // Load 1000 items at once
      console.log("API Response:", response);

      if (response?.data?.status === 1 && Array.isArray(response.data.data.folders)) {
        setUploadedDocuments(response.data.data.folders);
        setFilteredDocuments(response.data.data.folders);

        // Disable pagination since we're loading all data
        setCurrentPage(0);
        setTotalPages(1); // Only one page since we loaded everything
      } else {
        toast.warn("No documents found!", { position: "top-right" });
        setUploadedDocuments([]);
        setFilteredDocuments([]);
        setCurrentPage(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Error fetching documents!", { position: "top-right" });
      setUploadedDocuments([]);
      setFilteredDocuments([]);
      setCurrentPage(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderDocuments = async (folderId, page = 0) => {
    setLoading(true);
    try {
      console.log("🔄 Fetching folder documents:", { folderId, page });

      const response = await PostService.getFolderDocuments(folderId, page, 80);
      console.log("✅ Folder Documents API Response:", response.data);

      if (response?.data?.status === 1) {
        setCurrentFolder(response.data.data);
      } else {
        console.log("❌ API returned status 0:", response?.data);
        toast.warn("No documents found in folder!", { position: "top-right" });
      }
    } catch (error) {
      console.error("❌ Folder Documents API Error:", error)
      console.error("❌ Error response:", error.response?.data);
      toast.error("Error fetching folder documents!", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) {
      setFiles([]);
      setTotalMB(0);
      return;
    }

    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > 200 * 1024 * 1024 // 200MB
    );
    if (oversizedFiles.length > 0) {
      toast.warn("Files larger than 200MB are not allowed!", { position: "top-right" });
      return;
    }

    setFiles(selectedFiles);

    const totalSize = selectedFiles.reduce((total, file) => total + file.size, 0);
    setTotalMB((totalSize / (1024 * 1024)).toFixed(2));
  };

  const handleUpload = async () => {
    if (files.length === 0 || !documentName.trim()) {
      toast.warn(
        files.length === 0 ? "Please select files to upload!" : "Please enter a document name!",
        { position: "top-right" }
      );
      return;
    }
    if (!selectedFolderId && !newFolderName.trim()) {
      toast.warn("Please select a folder or create a new one!", { position: "top-right" });
      return;
    }

    setUploadProgress(0);
    setUploadedMB(0);
    setUploadSpeed(0);

    let lastLoaded = 0;
    let lastTime = Date.now();

    const config = {
      onUploadProgress: (progressEvent) => {
        if (!progressEvent.total) return; // safety for some browsers

        const { loaded, total } = progressEvent;
        const progress = Math.round((loaded * 100) / total);
        setUploadProgress(progress);
        setUploadedMB((loaded / (1024 * 1024)).toFixed(2));

        const now = Date.now();
        const deltaTime = (now - lastTime) / 1000;
        const deltaLoaded = loaded - lastLoaded;

        if (deltaTime > 0 && deltaLoaded >= 0) {
          setUploadSpeed((deltaLoaded / (1024 * 1024) / deltaTime).toFixed(2));
        }

        lastLoaded = loaded;
        lastTime = now;
      },
    };

    setUploading(true);

    const formData = new FormData();
    files.forEach((file) => formData.append("mediaTypeFile", file));
    formData.append("name", documentName);
    formData.append("folderId", selectedFolderId || "");
    formData.append("folderName", newFolderName);
    formData.append("tag", tags);

    try {
      const response = await PostService.uploadDocument(formData, config);
      if (response?.data?.status === 1 && response.data.data) {
        toast.success("Upload successful!", { position: "top-right" });
        setFiles([]);
        setDocumentName("");
        setSelectedFolderId(null);
        setNewFolderName("");
        setTags("");
        fetchDocuments(currentPage);
        setActiveSection("view");
      } else {
        toast.error("Upload failed!", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Upload error!", { position: "top-right" });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadedMB(0);
      setUploadSpeed(0);
      setTotalMB(0);
    }
  };

  const handleCopyLink = (folderId, documentId) => {
    const link = `${API_URL}api/viewDocument/${folderId}/${documentId}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success("Link copied to clipboard!", { position: "top-right" });
    });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDocSearch = (e) => {
    setDocSearchQuery(e.target.value);
  };

  const handleFolderDoubleClick = (folder) => {
    setCurrentFolder(folder);
    setDocSearchQuery("");
    setFilteredFolderDocuments(folder.documents || []);
  };

  const handleFolderDocPageChange = (newPage) => {
    if (currentFolder) {
      fetchFolderDocuments(currentFolder.folderId, newPage);
    }
  };

  // ----- Editing Handlers for Folders -----
  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditingFolder(folder);
    setEditName(folder.folderName);
  };

  const handleFolderUpdate = async (folder) => {
    try {
      const response = await PostService.updateDetails({
        type: "folder",
        folderId: folder.folderId,
        folderName: editName
      });
      if (response?.data?.status === 1) {
        toast.success("Folder updated successfully");
        fetchDocuments(currentPage);
        setEditingFolder(null);
      } else {
        toast.error("Folder update failed");
      }
    } catch (error) {
      toast.error("Error updating folder");
    }
  };

  const performFolderDelete = async (folder) => {
    try {
      const response = await PostService.deleteDetails({
        type: "folder",
        folderId: folder.folderId
      });
      if (response?.data?.status === 1) {
        toast.success("Folder deleted successfully");
        fetchDocuments(currentPage);
      } else {
        toast.error("Folder deletion failed");
      }
    } catch (error) {
      toast.error("Error deleting folder");
    } finally {
      setConfirmData({ visible: false, message: '', onConfirm: null });
    }
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setConfirmData({
      visible: true,
      message: `Are you sure you want to delete this folder?`,
      onConfirm: () => performFolderDelete(folder)
    });
  };

  // ----- Editing Handlers for Documents -----
  const handleDocumentEdit = (document, e) => {
    e.stopPropagation();
    setEditingDocument(document);
    setEditName(document.name);
  };

  const handleDocumentUpdate = async (document) => {
    try {
      const response = await PostService.updateDetails({
        type: "document",
        fileId: document.fileId,
        fileName: editName
      });
      if (response?.data?.status === 1) {
        toast.success("Document updated successfully");
        if (currentFolder) {
          fetchFolderDocuments(currentFolder.folderId, currentFolder.docPagination.currentPage);
        }
        setEditingDocument(null);
      } else {
        toast.error("Document update failed");
      }
    } catch (error) {
      toast.error("Error updating document");
    }
  };

  const performDocumentDelete = async (document) => {
    try {
      const response = await PostService.deleteDetails({
        type: "document",
        fileId: document.fileId
      });
      if (response?.data?.status === 1) {
        toast.success("Document deleted successfully");
        if (currentFolder) {
          fetchFolderDocuments(currentFolder.folderId, currentFolder.docPagination.currentPage);
        }
      } else {
        toast.error("Document deletion failed");
      }
    } catch (error) {
      toast.error("Error deleting document");
    } finally {
      setConfirmData({ visible: false, message: '', onConfirm: null });
    }
  };

  const handleDocumentDelete = (document, e) => {
    e.stopPropagation();
    setConfirmData({
      visible: true,
      message: `Are you sure you want to delete this document?`,
      onConfirm: () => performDocumentDelete(document)
    });
  };

  return (
    <div className="my-documents-container">
      <div className="header-section">
        {currentFolder && (
          <button className="back-button" onClick={() => setCurrentFolder(null)}>
            Back
          </button>
        )}
        {activeSection === 'upload' ? (
          <h1 className="page-title">My Documents</h1>
        ) : (
          <h1 className="view-title">
            {currentFolder ? "" : 'My Documents'}
          </h1>
        )}
        {activeSection === 'view' && currentFolder && currentFolder.tags && (
          <div className="folder-tags">
            {currentFolder.tags.split(',').map(tag => `#${tag.trim()}`).join(' ')}
          </div>
        )}
        {activeSection === 'view' && !currentFolder && (
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search folders by name..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
        )}
      </div>

      {!currentFolder && (
        <div className="section-switcher">
          <button
            className={`switch-button ${activeSection === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveSection('upload')}
          >
            Upload Documents
          </button>
          <button
            className={`switch-button ${activeSection === 'view' ? 'active' : ''}`}
            onClick={() => setActiveSection('view')}
          >
            View Documents
          </button>
        </div>
      )}

      {/* Upload Progress Bar */}
      {/* {uploading && (
        <div className="upload-progress-container">
          <div className="upload-progress-header">
            <span>Uploading {files.length} file(s)...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="upload-progress-bar">
            <div 
              className="upload-progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="upload-progress-details">
            <span>{uploadedMB} MB of {totalMB} MB</span>
            <span>{uploadSpeed} MB/s</span>
          </div>
        </div>
      )} */}

      {/* Upload Section */}
      {activeSection === 'upload' && (
        <div className="upload-section">
          {uploading && (
            <div className="upload-progress-container">
              <div className="upload-progress-header">
                <span>Uploading {files.length} file(s)...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="upload-progress-bar">
                <div
                  className="upload-progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="upload-progress-details">
                <span>{uploadedMB} MB of {totalMB} MB</span>
                <span>{uploadSpeed} MB/s</span>
              </div>
            </div>
          )}
          <div className="upload-box">
            <div className="input-group">
              <label htmlFor="document-name">Document Name</label>
              <input
                type="text"
                id="document-name"
                placeholder="Enter document name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className="document-name-input"
              />
            </div>
            <div className="input-group">
              <label htmlFor="folder-select">Select Folder</label>
              <select
                id="folder-select"
                value={selectedFolderId || ''}
                onChange={(e) => {
                  setSelectedFolderId(e.target.value);
                  setNewFolderName('');
                }}
                className="folder-select"
              >
                <option value="">Select a folder</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.folderName}</option>
                ))}
              </select>
            </div>
            {!selectedFolderId && (
              <div className="input-group">
                <label htmlFor="new-folder">Create New Folder</label>
                <input
                  type="text"
                  id="new-folder"
                  placeholder="Enter new folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="new-folder-input"
                />
              </div>
            )}
            <div className="input-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                placeholder="Enter tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="tags-input"
              />
            </div>
            <div className="file-upload-container">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileChange}
                className="file-input"
              />

              <label htmlFor="file-upload" className="upload-label">
                <div className="upload-content">
                  <span className="upload-icon">📁</span>
                  <span className="upload-text">
                    {files.length > 0 ? `${files.length} file(s) selected` : "Select Files to Upload"}
                  </span>
                </div>
              </label>
            </div>
            <button onClick={handleUpload} disabled={uploading || files.length === 0} className="upload-button">
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {/* Folder View */}
      {activeSection === 'view' && !currentFolder && (
        <>
          <div className="documents-grid">
            {loading ? (
              <div className="loading-message">Fetching documents...</div>
            ) : filteredDocuments.length > 0 ? (
              filteredDocuments.map((folder) => (
                <div
                  key={folder.folderId}
                  className="folder-section"
                  onDoubleClick={() => handleFolderDoubleClick(folder)}
                >
                  <div className="card-icons">
                    <button className="icon-button edit-button" onClick={(e) => handleFolderEdit(folder, e)}><MdOutlineEdit /></button>
                    <button className="icon-button delete-button" onClick={(e) => handleFolderDelete(folder, e)}><MdDelete /></button>
                  </div>
                  <div className="folder-icon-container">
                    <img src={folderIcon} alt="Folder Icon" className="folder-icon" />
                  </div>
                  <div className="folder-name-below">
                    {editingFolder && editingFolder.folderId === folder.folderId ? (
                      <div className="edit-form">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Enter folder name"
                        />
                        <div>
                          <button onClick={() => handleFolderUpdate(folder)}>Save</button> <button onClick={() => setEditingFolder(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      folder.folderName || 'Unnamed Folder'
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-documents">
                <p>No folders found.</p>
                <button className="upload-cta" onClick={() => setActiveSection('upload')}>
                  Upload
                </button>
              </div>
            )}
          </div>
          {/* <div className="modern-pagination">
            <button
              disabled={currentPage === 0}
              onClick={() => fetchDocuments(currentPage - 1)}
              className="pagination-button"
            >
              Previous
            </button>

            {totalPages > 0 && Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => fetchDocuments(i)}
                className={`pagination-button ${i === currentPage ? 'active' : ''}`}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => fetchDocuments(currentPage + 1)}
              className="pagination-button"
            >
              Next
            </button>
          </div> */}
        </>
      )}

      {/* Inside a Folder (Document View) */}
      {activeSection === 'view' && currentFolder && (
        <div className="folder-documents-view">
          <div className="doc-search-bar">
            <input
              type="text"
              placeholder="Search documents by name or tags..."
              value={docSearchQuery}
              onChange={handleDocSearch}
              className="search-input"
            />
          </div>
          {loading && (
            <div className="loader-overlay">
              <div className="spinner"></div>
            </div>
          )}
          <div className="documents-grid">
            {filteredFolderDocuments && filteredFolderDocuments.length > 0 ? (
              filteredFolderDocuments.map((document) => (
                <div key={document.id} className="document-card">
                  <div className="card-icons">
                    <button className="icon-button edit-button" onClick={(e) => handleDocumentEdit(document, e)}><MdOutlineEdit /></button>
                    <button className="icon-button delete-button" onClick={(e) => handleDocumentDelete(document, e)}><MdDelete /></button>
                  </div>
                  <div className="document-thumbnail">
                    <img
                      src={document.fileName?.endsWith('.pdf') ? pdfIcon : docIcon}
                      alt={document.fileName?.endsWith('.pdf') ? 'PDF Icon' : 'Document Icon'}
                      className="document-icon"
                    />
                  </div>
                  <div className="document-details">
                    {editingDocument && editingDocument.fileId === document.fileId ? (
                      <div className="edit-forms">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Enter document name"
                        />
                        <div>
                          <button onClick={() => handleDocumentUpdate(document)}>Save</button>  <button onClick={() => setEditingDocument(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="document-name">{document.name}</span>
                        <div className="document-actions">
                          <button onClick={() => handleCopyLink(currentFolder.folderId, document.id)} className="copy-link-button">
                            Copy Link
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-documents">No documents in this folder.</p>
            )}
          </div>
          {/* <div className="modern-pagination">
            <button
              disabled={currentFolder.docPagination.currentPage === 0}
              onClick={() => handleFolderDocPageChange(currentFolder.docPagination.currentPage - 1)}
              className="pagination-button"
            >
              Previous
            </button>
            {Array.from({ length: currentFolder.docPagination.totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handleFolderDocPageChange(i)}
                className={`pagination-button ${i === currentFolder.docPagination.currentPage ? 'active' : ''}`}
                disabled={i === currentFolder.docPagination.currentPage}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentFolder.docPagination.currentPage + 1 >= currentFolder.docPagination.totalPages}
              onClick={() => handleFolderDocPageChange(currentFolder.docPagination.currentPage + 1)}
              className="pagination-button"
            >
              Next
            </button>
          </div> */}
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      <ConfirmDialog
        visible={confirmData.visible}
        message={confirmData.message}
        onConfirm={confirmData.onConfirm}
        onCancel={() => setConfirmData({ visible: false, message: '', onConfirm: null })}
      />
    </div>
  );
};

export default MyDocuments;